import { lazy, Suspense, type ComponentType } from 'react';
import { GUIDE_PAGES } from '@snakefeast/shared';
import { Spinner } from '../components/ui';

type Loader = () => Promise<{ default: ComponentType }>;

/** Each content page is its own chunk: the game never downloads them, and they never download the game. */
const LOADERS: Record<string, Loader> = {
  '/how-to-play': () => import('./pages/HowToPlay'),
  '/multiplayer-snake-game': () => import('./pages/MultiplayerSnake'),
  '/about': () => import('./pages/About'),
  '/contact': () => import('./pages/Contact'),
  '/delete-account': () => import('./pages/DeleteAccount'),
  '/privacy': () => import('./pages/Privacy'),
  '/terms': () => import('./pages/Terms'),
  // The guides share one chunk: the hub and every article.
  '/guides': () => import('./Guide').then((m) => ({ default: m.GuidesHub })),
  ...Object.fromEntries(GUIDE_PAGES.map((p): [string, Loader] => [p.path, () => import('./Guide').then((m) => ({ default: m.GUIDES[p.path]! }))])),
};

const loaded = new Map<string, ComponentType>();
const lazies = new Map<string, ComponentType>();

/**
 * Fetch a content page's chunk before the first render. The pre-rendered HTML stays on screen meanwhile,
 * and React then swaps in identical markup in one commit, with no spinner in between.
 */
export async function preloadContent(path: string): Promise<void> {
  const load = LOADERS[path];
  if (load && !loaded.has(path)) loaded.set(path, (await load()).default);
}

export function ContentPage({ path }: { path: string }) {
  const Ready = loaded.get(path);
  if (Ready) return <Ready />;
  let Lazy = lazies.get(path);
  if (!Lazy) {
    Lazy = lazy(LOADERS[path]!);
    lazies.set(path, Lazy);
  }
  return (
    <Suspense fallback={<div className="empty"><Spinner /></div>}>
      <Lazy />
    </Suspense>
  );
}
